import VirtualKeyboardKey from "./VirtualKeyboardKey";
import VirtualKeyboardActionButton from "./VirtualKeyboardActionButton";
import React, { useState } from "react";
import { MdClose } from "react-icons/md";
import {
  KEYBOARD_GROUP_SEPARATOR,
  KEYBOARD_ROW_GAP,
  keyboardLayouts,
  type KeyboardLanguage,
} from "@/config/keyboardLayouts";
import { useTranslation } from "react-i18next";

const LAYOUT_LANGUAGES = ["japanese", "english"] as const;

interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onClear: () => void;
  onClose: () => void;
  onRight: () => void;
  onLeft: () => void;
  // 入力欄から離れているため、入力中の文字と該当件数をキーボード上にも出す
  value?: string;
  caretIndex?: number;
  placeholder?: string;
  resultCount?: number;
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  onKeyPress,
  onBackspace,
  onClear,
  onClose,
  onRight,
  onLeft,
  value = "",
  caretIndex,
  placeholder,
  resultCount,
}) => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState<KeyboardLanguage>(
    i18n.resolvedLanguage === "en" ? "english" : "japanese",
  );

  // 言語が変更されたときにキーボードレイアウトを更新
  React.useEffect(() => {
    if (i18n.resolvedLanguage === "en") {
      setLanguage("english");
    } else {
      setLanguage("japanese");
    }
  }, [i18n.resolvedLanguage]);

  const caretPosition = Math.min(Math.max(caretIndex ?? value.length, 0), value.length);
  const renderKey = (
    char: string,
    rIndex: number,
    cIndex: number,
    spacerClassName: string = "h-16 w-16",
  ) => {
    const isSpacer = char === "　"; // 全角スペースは表示しない
    if (isSpacer) {
      return <div key={`${rIndex}-${cIndex}`} aria-hidden="true" className={spacerClassName} />;
    }

    return (
      <VirtualKeyboardKey key={`${rIndex}-${cIndex}`} onClick={() => onKeyPress(char)}>
        {char}
      </VirtualKeyboardKey>
    );
  };

  // かなは五十音の列 (あ行・か行…) がまとまりなので縦を詰めて横を空ける。英字は行がまとまり
  const renderLayout = (lang: (typeof LAYOUT_LANGUAGES)[number]) => {
    const layoutRows = keyboardLayouts[lang];
    const isKana = lang === "japanese";
    return (
      <div className={`flex flex-col ${isKana ? "gap-2" : "gap-3"}`}>
        {layoutRows.map((row, rowIndex) => {
          if (row.length === 1 && row[0] === KEYBOARD_ROW_GAP) {
            return (
              <div
                key={rowIndex}
                aria-hidden="true"
                className={`${isKana ? "h-5" : "h-3"} shrink-0`}
              />
            );
          }

          const chars: readonly string[] = row;
          if (chars.includes(KEYBOARD_GROUP_SEPARATOR)) {
            const groups: { char: string; index: number }[][] = [[]];
            chars.forEach((char, index) => {
              if (char === KEYBOARD_GROUP_SEPARATOR) groups.push([]);
              else groups[groups.length - 1].push({ char, index });
            });
            return (
              <div key={rowIndex} className="flex shrink-0 justify-between">
                {groups.map((group, groupIndex) => (
                  <div key={groupIndex} className="flex gap-1.5">
                    {group.map(({ char, index }) => renderKey(char, rowIndex, index))}
                  </div>
                ))}
              </div>
            );
          }

          if (!isKana) {
            // 英語最終行は記号の前に隙間を入れて字下げする
            const isLastRow = rowIndex === layoutRows.length - 1;
            return (
              <div
                key={rowIndex}
                className={`flex shrink-0 justify-center gap-2.5 ${isLastRow ? "pl-6" : ""}`}
              >
                {row.map((char, charIndex) =>
                  renderKey(
                    char,
                    rowIndex,
                    charIndex,
                    isLastRow && char === "　" ? "h-16 w-6" : "h-16 w-16",
                  ),
                )}
              </div>
            );
          }

          return (
            <div key={rowIndex} className="mx-auto grid shrink-0 grid-cols-10 gap-4">
              {row.map((char, charIndex) => renderKey(char, rowIndex, charIndex))}
            </div>
          );
        })}
      </div>
    );
  };

  const echoRow = (
    <div className="flex h-12 w-0 min-w-full items-center justify-between gap-6">
      <p className="min-w-0 flex-1 truncate text-[32px] font-bold leading-none text-TextPrimary">
        {value ? (
          <>
            {value.slice(0, caretPosition)}
            <span
              aria-hidden="true"
              className="mx-px inline-block h-[1em] w-[3px] align-top bg-MiraiPrimary"
            />
            {value.slice(caretPosition)}
          </>
        ) : (
          <span className="text-[20px] font-normal text-TextSecondary">{placeholder}</span>
        )}
      </p>
      {value && resultCount !== undefined && (
        <p className="shrink-0 text-[20px] font-bold text-MiraiPrimary" aria-live="polite">
          {t("該当 {{count}}件", { count: resultCount })}
        </p>
      )}
    </div>
  );

  return (
    <div className="max-h-[calc(100vh-2rem)] w-full overflow-y-auto overflow-x-hidden rounded-[2rem] border border-Border bg-white px-6 py-6 shadow-[0_16px_48px_rgba(15,23,42,0.08)]">
      <div className="flex w-full items-stretch justify-center gap-12">
        {/* 左カラム*/}
        <div className="flex w-44 shrink-0 flex-col justify-between gap-8">
          <VirtualKeyboardActionButton onClick={onClose} className="w-full">
            <span className="inline-flex items-center gap-1.5">
              <MdClose className="size-6" aria-hidden="true" />
              <span>{t("閉じる")}</span>
            </span>
          </VirtualKeyboardActionButton>
          <div className="flex flex-col gap-2 self-stretch">
            <VirtualKeyboardActionButton
              onClick={() => setLanguage("japanese")}
              isSelected={language === "japanese"}
              className="w-full"
            >
              {t("かな")}
            </VirtualKeyboardActionButton>
            <VirtualKeyboardActionButton
              onClick={() => setLanguage("english")}
              isSelected={language === "english"}
              className="w-full"
            >
              {t("ABC・123")}
            </VirtualKeyboardActionButton>
          </div>
        </div>
        {/* 中央: 全レイアウトを同じセルに重ねて大きい方に寸法を揃え、切替で左右のボタンが動かないようにする */}
        <div className="z-10 grid shrink-0">
          {LAYOUT_LANGUAGES.map((lang) => {
            const isActive = lang === language;
            return (
              <div
                key={lang}
                className={`col-start-1 row-start-1 flex flex-col gap-3 self-start justify-self-center ${isActive ? "" : "invisible"}`}
                aria-hidden={!isActive || undefined}
                inert={!isActive}
              >
                {echoRow}
                {renderLayout(lang)}
              </div>
            );
          })}
        </div>

        {/* 右カラム: 入力中の文字を操作するボタンを、文字表示の近くにまとめる */}
        <div className="flex w-44 shrink-0 flex-col gap-6">
          <div className="flex flex-col gap-2">
            <VirtualKeyboardActionButton onClick={onBackspace} className="w-full">
              {t("1文字消す")}
            </VirtualKeyboardActionButton>
            <VirtualKeyboardActionButton onClick={onClear} className="w-full">
              {t("すべて消す")}
            </VirtualKeyboardActionButton>
          </div>
          <div className="flex gap-2">
            <VirtualKeyboardActionButton
              onClick={onLeft}
              className="flex-1"
              aria-label={t("カーソルを左へ移動")}
            >
              ←
            </VirtualKeyboardActionButton>
            <VirtualKeyboardActionButton
              onClick={onRight}
              className="flex-1"
              aria-label={t("カーソルを右へ移動")}
            >
              →
            </VirtualKeyboardActionButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualKeyboard;
